// src/services/maintenance.ts
//
// Cutover maintenance-mode signal (GCP migration §8). Fully inert unless the
// backend has MAINTENANCE_MODE=true. Kept deliberately free of React and of the
// axios `api` instance (no import cycle) so the response interceptor — which is
// module-scope, not a component — can flip the whole SPA into the full-page
// maintenance screen, and so the screen can poll for recovery with plain fetch.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type Listener = (active: boolean) => void;

const listeners = new Set<Listener>();
let active = false;

/** Current known maintenance state (last value observed from the backend). */
export function isMaintenanceActive(): boolean {
  return active;
}

/** Update state and notify subscribers (no-op if unchanged). */
export function setMaintenanceActive(next: boolean): void {
  if (active === next) return;
  active = next;
  listeners.forEach((l) => {
    try {
      l(active);
    } catch {
      /* a bad subscriber must not break the others */
    }
  });
}

/** Subscribe to maintenance-state changes. Returns an unsubscribe fn. */
export function onMaintenanceChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Probe `GET /api/maintenance-status` (public, always available) and update the
 * shared state. Returns the resolved flag. On a network/5xx blip (e.g. the old
 * stack restarting as the flag clears) it keeps the current state rather than
 * flapping the UI.
 */
export async function probeMaintenance(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/api/maintenance-status`, { cache: 'no-store' });
    if (!res.ok) return active;
    const data = await res.json();
    const on = data?.maintenance === true;
    setMaintenanceActive(on);
    return on;
  } catch {
    return active;
  }
}
